import { User, Crown, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import type { Subscription } from "../types";
import { subscriptionApi } from "../lib/api";
import { getRedirectUrl } from "../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface SettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: SupabaseUser | null;
}

export function SettingsDialog({ isOpen, onClose, user }: SettingsDialogProps) {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  // Load subscription data when dialog opens and user is available
  useEffect(() => {
    if (isOpen && user) {
      loadSubscription();
    }
  }, [isOpen, user]);

  const loadSubscription = async () => {
    if (!user) return;

    setSubscriptionLoading(true);
    try {
      const response = await subscriptionApi.getSubscription();
      setSubscription(response.subscription);
    } catch (error) {
      console.error("Failed to load subscription:", error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleUpgrade = async () => {
    if (!user) return;

    setCheckoutLoading(true);
    try {
      const priceId = "price_1RSWEtPJpLJhOAugdPcygt4c";

      const response = await subscriptionApi.createCheckout({
        priceId,
        successUrl: getRedirectUrl('/generate?success=true'),
        cancelUrl: getRedirectUrl('/generate?canceled=true'),
      });

      // Redirect to Stripe checkout
      window.location.href = response.checkoutUrl;
    } catch (error) {
      console.error("Failed to create checkout session:", error);
      alert("Failed to start checkout process. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!user) return;

    setPortalLoading(true);
    try {
      const response = await subscriptionApi.createPortalSession({
        returnUrl: getRedirectUrl('/generate'),
      });

      // Redirect to Stripe Customer Portal
      window.location.href = response.portalUrl;
    } catch (error) {
      console.error("Failed to create portal session:", error);
      alert("Failed to open subscription management. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };

  const isProUser =
    subscription?.plan_type === "pro" && subscription?.status === "active";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[600px] max-w-[90vw] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6">
          {/* Profile Section */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center space-x-3 mb-4">
              <User size={20} className="text-primary" />
              <h3 className="text-lg font-medium text-foreground">Profile</h3>
            </div>

            {user ? (
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  {user.user_metadata?.avatar_url ? (
                    <img
                      src={user.user_metadata.avatar_url}
                      alt="Profile"
                      className="w-16 h-16 rounded-full"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                      <User size={24} className="text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <h4 className="text-foreground font-medium">
                      {user.user_metadata?.full_name || "Unknown User"}
                    </h4>
                    <p className="text-muted-foreground text-sm">{user.email}</p>
                    <p className="text-muted-foreground text-xs">
                      Member since{" "}
                      {new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">
                      Full Name
                    </Label>
                    <Input
                      type="text"
                      value={user.user_metadata?.full_name || ""}
                      disabled
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">
                      Email
                    </Label>
                    <Input
                      type="email"
                      value={user.email || ""}
                      disabled
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <User size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Not signed in</p>
                <p className="text-muted-foreground text-sm">
                  Sign in to manage your profile
                </p>
              </div>
            )}
          </div>

          {/* Subscription Section */}
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Crown size={20} className="text-warning" />
              <h3 className="text-lg font-medium text-foreground">Subscription</h3>
              {subscriptionLoading && (
                <Loader2 size={16} className="animate-spin text-muted-foreground" />
              )}
            </div>

            {subscriptionLoading ? (
              <div className="text-center py-8">
                <Loader2
                  size={32}
                  className="animate-spin text-muted-foreground mx-auto mb-2"
                />
                <p className="text-muted-foreground">Loading subscription...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Current Plan */}
                <div
                  className={`p-4 rounded-lg border ${
                    isProUser
                      ? "bg-gradient-to-r from-warning/10 to-accent/10 border-warning/20"
                      : "bg-card border-border"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-foreground font-medium flex items-center">
                        {isProUser && (
                          <Crown size={16} className="text-warning mr-2" />
                        )}
                        {isProUser ? "Pro Tier" : "Free Tier"}
                        {isProUser && !subscription?.cancel_at_period_end && (
                          <span className="ml-2 px-2 py-1 bg-warning/20 text-warning text-xs rounded">
                            Active
                          </span>
                        )}
                        {isProUser && subscription?.cancel_at_period_end && (
                          <span className="ml-2 px-2 py-1 bg-warning/20 text-warning text-xs rounded">
                            Canceling
                          </span>
                        )}
                        {subscription?.status === "past_due" && (
                          <span className="ml-2 px-2 py-1 bg-destructive/20 text-destructive text-xs rounded">
                            Payment Required
                          </span>
                        )}
                      </h4>
                      <p className="text-muted-foreground text-sm">
                        {isProUser
                          ? subscription?.cancel_at_period_end 
                            ? subscription?.current_period_end
                              ? `Access until ${new Date(subscription.current_period_end).toLocaleDateString()}`
                              : "Access until subscription ends"
                            : "Unlimited generation and premium features"
                          : "Limited sound generation and storage"}
                      </p>
                      {subscription?.cancel_at_period_end && (
                        <div className="mt-2 p-3 bg-warning/10 border border-warning/20 rounded-lg">
                          <p className="text-warning text-sm font-medium">
                            ⚠️ Subscription Cancelled
                          </p>
                          <p className="text-warning/80 text-xs mt-1">
                            {subscription?.current_period_end 
                              ? `Your Pro access will end on ${new Date(subscription.current_period_end).toLocaleDateString()}. After this date, your account will revert to the free tier.`
                              : "Your subscription has been cancelled and will end at the current billing period."
                            }
                          </p>
                          {subscription?.current_period_end && (
                            <p className="text-warning/60 text-xs mt-2">
                              To reactivate, click "Manage Subscription" below and resume your subscription.
                            </p>
                          )}
                        </div>
                      )}
                      <ul className="text-muted-foreground text-xs mt-2 space-y-1">
                        {isProUser ? (
                          <>
                            <li>• Unlimited sound generations</li>
                            <li>• Unlimited projects and folders</li>
                            <li>• High-quality audio output</li>
                            <li>• Priority support</li>
                            <li>• Advanced sound customization</li>
                          </>
                        ) : (
                          <>
                            <li>• 10 sound generations per day</li>
                            <li>• 5 projects maximum</li>
                            <li>• Basic audio quality</li>
                          </>
                        )}
                      </ul>
                      {subscription?.current_period_end && isProUser && !subscription?.cancel_at_period_end && (
                        <p className="text-muted-foreground text-xs mt-2">
                          Renews on: {new Date(subscription.current_period_end).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-foreground">
                        {isProUser ? "$9.99" : "$0"}
                      </div>
                      <div className="text-xs text-muted-foreground">per month</div>
                    </div>
                  </div>
                </div>

                {/* Pro Tier Upgrade (show only if not already pro) */}
                {!isProUser && (
                  <div className="p-4 bg-gradient-to-r from-warning/10 to-accent/10 rounded-lg border border-warning/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-foreground font-medium flex items-center">
                          <Crown size={16} className="text-warning mr-2" />
                          Upgrade to Pro
                        </h4>
                        <p className="text-muted-foreground text-sm">
                          Unlock unlimited generation and premium features
                        </p>
                        <ul className="text-muted-foreground text-xs mt-2 space-y-1">
                          <li>• Unlimited sound generations</li>
                          <li>• Unlimited projects and folders</li>
                          <li>• High-quality audio output</li>
                          <li>• Priority support</li>
                          <li>• Advanced sound customization</li>
                        </ul>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold text-foreground">
                          $9.99
                        </div>
                        <div className="text-xs text-muted-foreground">per month</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Button */}
                {!isProUser ? (
                  <Button
                    onClick={handleUpgrade}
                    disabled={checkoutLoading || !user}
                    className="w-full px-4 py-3"
                  >
                    {checkoutLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin mr-2" />
                        Creating checkout...
                      </>
                    ) : (
                      <>
                        <Crown size={16} className="mr-2" />
                        Upgrade to Pro
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="text-center py-2">
                      {subscription?.cancel_at_period_end ? (
                        <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
                          <p className="text-warning text-sm font-medium">
                            ⚠️ Subscription Scheduled for Cancellation
                          </p>
                          <p className="text-warning/80 text-xs mt-1">
                            {subscription?.current_period_end 
                              ? `Your Pro access will continue until ${new Date(subscription.current_period_end).toLocaleDateString()}`
                              : "Your subscription will end at the current billing period"
                            }
                          </p>
                          <p className="text-warning/60 text-xs mt-2">
                            Use "Manage Subscription" below to reactivate if you change your mind.
                          </p>
                        </div>
                      ) : (
                        <p className="text-success text-sm">
                          ✅ You're subscribed to Pro! Thank you for your support.
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={handleManageSubscription}
                      disabled={portalLoading}
                      variant="secondary"
                      className="w-full"
                      size="sm"
                    >
                      {portalLoading ? (
                        <>
                          <Loader2 size={14} className="animate-spin mr-2" />
                          Loading...
                        </>
                      ) : (
                        "Manage Subscription"
                      )}
                    </Button>
                  </div>
                )}

                <div className="text-center">
                  <p className="text-muted-foreground text-xs">
                    Secure payment processing powered by Stripe
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
